import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  createMobileNotification,
  createNotification,
  mobileNotificationEventQuery,
  mobileNotificationEventsQuery,
  mobileNotificationFormQuery,
  mobileNotificationsShellQuery,
  notificationEventQuery,
  notificationEventsQuery,
  notificationFormQuery,
  notificationsShellQuery,
  removeMobileNotification,
  removeNotification,
  updateMobileNotification,
  updateNotification,
  type NotificationEvent,
  type NotificationInput,
  type NotificationOptions,
  type NotificationRule,
  type NotificationSaved,
} from '../../api/settingsEaseedeskNotifications'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { SelectPicker } from '../../ui/SelectPicker'

/**
 * Settings → Easeedesk → Email notifications si Mobile notifications.
 *
 * Element cu element fata de `version-3/easeedesk_settings/notifications/
 * notifications/{index,list,notifications,create_form}.php` si
 * `.../mobile/{index,list,notifications,create_form}.php`, si gest cu gest fata
 * de scripturile lor: casuta de tip de tichet care reincarca lista, `+` care
 * deschide un singur rand odata (`expandDetails`), panourile de reguli cu Save
 * si Remove, si fereastra de adaugat.
 *
 * =============================================================================
 * CE HOTARASTE ECRANUL ASTA
 * =============================================================================
 *
 * Fiecare panou e un rand din `it_easeedesk_notifications_scheme`, adica o
 * regula pe care o citeste coada de instiintari a tichetelor: cine primeste,
 * in ce limba, cu ce sablon, de la ce nume si ce adresa, cu semnatura sau fara,
 * sub ce conditii si pentru ce firme. Nu e un ecran de infatisare - e chiar
 * lista celor care primesc emailuri.
 *
 * =============================================================================
 * TREI LUCRURI CARE ARATA ALTFEL DECAT IN PORTAL, SI DE CE
 * =============================================================================
 *
 * FEREASTRA DE SCHIMBAT NU EXISTA NICI AICI. `#editNotificationModal` statea in
 * macheta veche si `index-1.js` stia sa-l umple din `edit_notification_form`,
 * dar niciun buton nu-l deschidea: regulile se schimba in panoul desfasurat.
 * Capul de api e mutat (`GET .../notifications/{id}`), fereastra nu.
 *
 * CASUTA DE FIRME e iar `selectpicker`-ul din portal. Randurile de mai sus au
 * stat o vreme pe casuta browserului, fiindca `SelectPicker` nu stia decat o
 * singura alegere; stie `multiple` de cand l-a cerut Oncall, cu bifa pe rand,
 * cautare si "n selectate". Aceleasi atribute ca in `notifications.php`:
 * `data-actions-box`, `data-size="15"`, `data-selected-text-format="count > 3"`
 * si titlul "All companies". `name` ramane `companies[]` si <select>-ul
 * adevarat ramane in pagina, ascuns - POST-ul pleaca neschimbat.
 *
 * BIFA DE SEMNATURA e o casuta obisnuita, nu iCheck.
 */

/** `escalation_type` din ecranul vechi: doua optiuni, si niciuna "orice". */
const ESCALATION_CHOICES: { value: string; label: string }[] = [
  { value: '0', label: 'Department' },
  { value: '1', label: 'User' },
]

/** `ticket_status` pus pe "orice" (`Plan::ANY`). */
const ANY_STATUS = '-1'

interface Wiring {
  mobile: boolean
  shellQuery: ReturnType<typeof notificationsShellQuery>
  eventsQuery(applicationType: number, ticketType: number): ReturnType<typeof notificationEventsQuery>
  eventQuery(ticketType: number, event: number): ReturnType<typeof notificationEventQuery>
  formQuery(ticketType: number, event: number): ReturnType<typeof notificationFormQuery>
  create(input: NotificationInput): Promise<NotificationSaved>
  update(id: number, input: NotificationInput): Promise<NotificationSaved>
  remove(id: number): Promise<NotificationSaved>
  /** Cheia de reimprospatat dupa o scriere. */
  key: string
}

const EMAIL: Wiring = {
  mobile: false,
  shellQuery: notificationsShellQuery(),
  eventsQuery: (_app, ticketType) => notificationEventsQuery(ticketType),
  eventQuery: notificationEventQuery,
  formQuery: notificationFormQuery,
  create: createNotification,
  update: updateNotification,
  remove: removeNotification,
  key: 'notifications',
}

const MOBILE: Wiring = {
  mobile: true,
  shellQuery: mobileNotificationsShellQuery(),
  eventsQuery: mobileNotificationEventsQuery,
  eventQuery: mobileNotificationEventQuery,
  formQuery: mobileNotificationFormQuery,
  create: createMobileNotification,
  update: updateMobileNotification,
  remove: removeMobileNotification,
  key: 'mobile_notifications',
}

/*
 * `subtitle` e NUMAI partea ingrosata: prefixul il pune `Screen`
 * ("Manage, create or edit ticket " / "Manage mobile "). Portarea daduse
 * subtitluri care repetau prefixul, si ecranul scria "Manage, create or edit
 * ticket ticket types" si "Manage mobile mobile notifications". Machetele
 * vechi scriu `<strong>types</strong>` si `<strong>notifications</strong>`.
 */
export function NotificationsPage() {
  return <Screen wiring={EMAIL} subtitle="types" />
}

export function MobileNotificationsPage() {
  return <Screen wiring={MOBILE} subtitle="notifications" />
}

function Screen({ wiring, subtitle }: { wiring: Wiring; subtitle: string }) {
  const shell = useQuery(wiring.shellQuery)
  const [ticketType, setTicketType] = useState(0)
  const [app, setApp] = useState(0)
  const [open, setOpen] = useState(0)
  const [adding, setAdding] = useState<NotificationEvent | null>(null)

  /* Macheta veche declansa `change` pe casuta indata ce se incarca, deci
     primul tip din lista era ales de la sine. */
  useEffect(() => {
    if (ticketType === 0 && shell.data && shell.data.ticketTypes.length > 0) {
      setTicketType(shell.data.ticketTypes[0]!.ID)
    }
  }, [shell.data, ticketType])

  useEffect(() => {
    if (!wiring.mobile || app !== 0) return
    const apps = shell.data?.apps ?? []
    if (apps.length > 0) setApp(apps[0]!.ID)
  }, [shell.data, app, wiring.mobile])

  const applicationType = wiring.mobile ? app : 1
  const list = useQuery(wiring.eventsQuery(applicationType, ticketType))

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed what="The notifications" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  const apps = shell.data.apps ?? []

  return (
    <div>
      <h4>
        <strong>Notifications</strong> settings
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        {wiring.mobile ? 'Manage mobile ' : 'Manage, create or edit ticket '}
        <strong>{subtitle}</strong>
      </label>
      <hr />

      <div className="row" style={{ marginBottom: 20 }}>
        <div className="col-md-12">
          <form className="form-inline" id="filter_notification_events" onSubmit={(e) => e.preventDefault()}>
            {wiring.mobile && (
              <div className="form-group">
                <label>Application type</label>{' '}
                <select
                  className="form-control input-sm"
                  name="application_type"
                  value={app}
                  onChange={(e) => {
                    setApp(Number(e.target.value))
                    setOpen(0)
                  }}
                >
                  {apps.map((one) => (
                    <option key={one.ID} value={one.ID}>
                      {one.name}
                    </option>
                  ))}
                </select>{' '}
              </div>
            )}
            <div className="form-group">
              <label>Ticket type</label>{' '}
              <select
                className="form-control input-sm"
                name="ticket_type"
                value={ticketType}
                onChange={(e) => {
                  setTicketType(Number(e.target.value))
                  setOpen(0)
                }}
              >
                {shell.data.ticketTypes.map((type) => (
                  <option key={type.ID} value={type.ID}>
                    {type.type_name}
                  </option>
                ))}
              </select>
            </div>
          </form>
        </div>
      </div>

      <div className="list-group">
        {list.error ? (
          <LoadFailed what="The events" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : (
          <table className="table table-hover ticket-events">
            <thead>
              <tr>
                <th className="a-left"></th>
                <th className="a-left">Ticket Event</th>
                <th className="a-left">Notifications</th>
                <th className="a-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data.events.map((event) => (
                <EventRow
                  key={event.ID}
                  event={event}
                  ticketType={ticketType}
                  wiring={wiring}
                  open={open === event.ID}
                  onToggle={() => setOpen(open === event.ID ? 0 : event.ID)}
                  onAdd={() => setAdding(event)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        id="addNotificationModal"
        open={adding !== null}
        onClose={() => setAdding(null)}
        labelledBy="addNotificationModalLabel"
        title={adding === null ? 'Add notification' : `Add notification to event "${adding.event}"`}
      >
        {adding !== null && (
          <CreateForm
            wiring={wiring}
            ticketType={ticketType}
            event={adding.ID}
            onDone={() => setAdding(null)}
          />
        )}
      </Modal>
    </div>
  )
}

function EventRow({
  event,
  ticketType,
  wiring,
  open,
  onToggle,
  onAdd,
}: {
  event: NotificationEvent
  ticketType: number
  wiring: Wiring
  open: boolean
  onToggle(): void
  onAdd(): void
}) {
  return (
    <>
      <tr className={open ? 'details' : undefined}>
        <td style={{ padding: '2px 6px', width: 50 }}>
          <i
            className={`fas ${open ? 'fa-minus-circle' : 'fa-plus-circle'} show-notifications`}
            aria-hidden="true"
            style={{ cursor: 'pointer' }}
            onClick={onToggle}
          ></i>
        </td>
        <td style={{ padding: '2px 6px' }}>
          {event.event.charAt(0).toUpperCase() + event.event.slice(1)}{' '}
          {event.description !== '' && (
            <i
              className="fas fa-info-circle color-blue font-12"
              style={{ cursor: 'default' }}
              data-toggle="tooltip"
              data-placement="top"
              title={event.description}
            >
              {' '}
            </i>
          )}
        </td>
        <td style={{ padding: '2px 6px' }}>
          <label className={`label ${event.notifications > 0 ? 'label-primary' : 'label-default'}`}>
            {event.notifications}
          </label>
        </td>
        <td style={{ padding: '4px 6px', width: 50 }}>
          <button className="btn btn-xs btn-success" type="button" onClick={onAdd}>
            <i className="fas fa-plus" aria-hidden="true"></i> Add
          </button>
        </td>
      </tr>
      {open && (
        <tr className="row-details">
          <td style={{ backgroundColor: '#FFF' }} colSpan={4}>
            <EventRules wiring={wiring} ticketType={ticketType} event={event.ID} />
          </td>
        </tr>
      )}
    </>
  )
}

/** `eventNotifications` / `mobileEventNotifications`: panourile de sub un eveniment. */
function EventRules({ wiring, ticketType, event }: { wiring: Wiring; ticketType: number; event: number }) {
  const detail = useQuery(wiring.eventQuery(ticketType, event))

  if (detail.error) return <LoadFailed what="The rules" error={detail.error} onRetry={() => void detail.refetch()} />
  if (!detail.data) return <Spinner />
  if (detail.data.notifications.length === 0) {
    return (
      <div className="note note-info">
        <i className="fas fa-info-circle" aria-hidden="true"></i> No notifications
      </div>
    )
  }

  return (
    <>
      {detail.data.notifications.map((rule) => (
        <RulePanel key={rule.ID} wiring={wiring} rule={rule} options={detail.data} />
      ))}
    </>
  )
}

/** Un panou: `notifications.php`, cu Save si Remove din `notifications-1.js`. */
function RulePanel({
  wiring,
  rule,
  options,
}: {
  wiring: Wiring
  rule: NotificationRule
  options: NotificationOptions
}) {
  const client = useQueryClient()
  const [draft, setDraft] = useState<NotificationInput>(() => fromRule(rule, wiring.mobile))

  useEffect(() => setDraft(fromRule(rule, wiring.mobile)), [rule, wiring.mobile])

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['settings', 'easeedesk', wiring.key] })
  }

  const save = useMutation({
    mutationFn: () => wiring.update(rule.ID, draft),
    onSuccess: (result) => {
      toast(cacheWarning(result) ?? 'Settings changed', 'Success')
      invalidate()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  const drop = useMutation({
    mutationFn: () => wiring.remove(rule.ID),
    onSuccess: (result) => {
      toast(cacheWarning(result) ?? 'Notification removed', 'Success')
      invalidate()
    },
    onError: () => toast('An error ocurred. Please try again later.', 'Error deleting', 'error'),
  })

  return (
    <div className="panel panel-default">
      <div className="panel-body">
        <form className="notification-form" method="post" onSubmit={(e) => e.preventDefault()}>
          <div className="row">
            <div className="col-md-3" style={{ marginBottom: 10 }}>
              <label>
                <i className="fa fa-user" aria-hidden="true"></i> Send to:
              </label>
              <RecipientSelect options={options} draft={draft} setDraft={setDraft} />
            </div>

            <div className="col-md-3" style={{ marginBottom: 10 }}>
              <label>
                <i className="fa fa-globe" aria-hidden="true"></i> Language:
              </label>
              <LanguageSelect options={options} draft={draft} setDraft={setDraft} />
            </div>

            <div className="col-md-3" style={{ marginBottom: 10 }}>
              <label>
                <i className="far fa-envelope-open" aria-hidden="true"></i> Template:
              </label>
              <TemplateSelect options={options} draft={draft} setDraft={setDraft} />
            </div>

            {!wiring.mobile && (
              <div className="col-md-3" style={{ marginBottom: 10 }}>
                <label style={{ width: '100%' }}>
                  <i className="fas fa-address-card" aria-hidden="true"></i> Assignee Signature:
                </label>
                {/* FARA `form-control`: in portal casuta asta era inlocuita de iCheck
                    (`notifications-1.js:2`), deci clasa nu se vedea niciodata. v4 nu
                    incarca iCheck, si atunci regula lui Bootstrap chiar prinde: casuta
                    se face cat tot randul, inalta de 34px, cu bifa pierduta in ea. */}
                <input
                  type="checkbox"
                  name="add_signature"
                  checked={draft.add_signature === 1}
                  onChange={(e) => setDraft({ ...draft, add_signature: e.target.checked ? 1 : 0 })}
                />
              </div>
            )}
          </div>

          {!wiring.mobile && (
            <div className="row">
              <div className="col-md-3" style={{ marginBottom: 10 }}>
                <label>
                  <i className="fa fa-share" aria-hidden="true"></i> From name:
                </label>
                <FromSelect field="from_name" draft={draft} setDraft={setDraft} />
              </div>

              <div className="col-md-3" style={{ marginBottom: 10 }}>
                <label>
                  <i className="fa fa-share" aria-hidden="true"></i> From email:
                </label>
                <FromSelect field="from_email" draft={draft} setDraft={setDraft} />
              </div>

              <Conditions options={options} draft={draft} setDraft={setDraft} />

              <div className="col-md-3" style={{ marginBottom: 10 }}>
                <label>
                  <i className="far fa-envelope-open" aria-hidden="true"></i> Companies:
                </label>
                <CompaniesSelect id="notificationCompanies" options={options} draft={draft} setDraft={setDraft} />
              </div>
            </div>
          )}
        </form>
      </div>
      <div className="panel-footer" style={{ padding: '10px 15px', backgroundColor: '#eee' }}>
        <button
          className="btn btn-success btn-xs save-notification"
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          Save
        </button>{' '}
        <button
          className="btn btn-danger btn-xs remove-notification"
          type="button"
          disabled={drop.isPending}
          onClick={() => drop.mutate()}
        >
          Remove
        </button>
      </div>
    </div>
  )
}

/** `add_notification_form` / `add_mobile_notification_form`. */
function CreateForm({
  wiring,
  ticketType,
  event,
  onDone,
}: {
  wiring: Wiring
  ticketType: number
  event: number
  onDone(): void
}) {
  const client = useQueryClient()
  const form = useQuery(wiring.formQuery(ticketType, event))
  const [draft, setDraft] = useState<NotificationInput | null>(null)

  useEffect(() => {
    if (!form.data || draft !== null) return
    setDraft({
      ticket_type: ticketType,
      event_id: event,
      user_type: form.data.userTypes[0]?.user_type_id ?? 0,
      language: form.data.languages[0] ?? 'RO',
      template_id: form.data.templates[0]?.ID ?? 0,
      /* Macheta de telefon trimite 2 pe amandoua, scris in campuri ascunse;
         cea de email lasa omul sa aleaga, si porneste de la 1. */
      from_name: wiring.mobile ? 2 : 1,
      from_email: wiring.mobile ? 2 : 1,
      add_signature: 0,
      conditions: {},
      companies: [],
    })
  }, [form.data, draft, ticketType, event, wiring.mobile])

  const create = useMutation({
    mutationFn: () => wiring.create(draft as NotificationInput),
    onSuccess: (result) => {
      toast(cacheWarning(result) ?? 'Notification created.', 'Success')
      void client.invalidateQueries({ queryKey: ['settings', 'easeedesk', wiring.key] })
      onDone()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (form.error) return <LoadFailed what="The form" error={form.error} onRetry={() => void form.refetch()} />
  if (!form.data || draft === null) return <Spinner />

  return (
    <form className="createNotification" method="post" onSubmit={(e) => e.preventDefault()}>
      <div className="form-group">
        <label className="control-label">
          User type (receiver): <i className="fa fa-question-circle color-blue" aria-hidden="true"></i>
        </label>
        <RecipientSelect options={form.data} draft={draft} setDraft={setDraft} big />
      </div>
      <div className="form-group">
        <label className="control-label">Language:</label>
        <LanguageSelect options={form.data} draft={draft} setDraft={setDraft} big />
      </div>
      <div className="form-group">
        <label className="control-label">Template:</label>
        <TemplateSelect options={form.data} draft={draft} setDraft={setDraft} big />
      </div>

      {!wiring.mobile && (
        <>
          <div className="form-group">
            <label className="control-label">Sender name:</label>
            <FromSelect field="from_name" draft={draft} setDraft={setDraft} big names />
          </div>
          <div className="form-group">
            <label className="control-label">Sender email:</label>
            <FromSelect field="from_email" draft={draft} setDraft={setDraft} big names />
          </div>
          <Conditions options={form.data} draft={draft} setDraft={setDraft} big />
          <div className="form-group">
            <label className="control-label">Insert assignee signature:</label>
            <select
              className="form-control"
              name="signature"
              value={draft.add_signature}
              onChange={(e) => setDraft({ ...draft, add_signature: Number(e.target.value) })}
            >
              <option value={0}>No</option>
              <option value={1}>Yes</option>
            </select>
          </div>
          <div className="form-group">
            <label className="control-label">Companies:</label>
            <CompaniesSelect id="notificationCompaniesEdit" options={form.data} draft={draft} setDraft={setDraft} />
          </div>
        </>
      )}

      <hr />
      <div className="form-group align-right">
        <button type="button" className="btn btn-default" onClick={onDone}>
          Close
        </button>{' '}
        <button type="button" className="btn btn-primary" disabled={create.isPending} onClick={() => create.mutate()}>
          Save
        </button>
      </div>
    </form>
  )
}

/* ---------------------------------------------------------------- casutele */

type Setter = (draft: NotificationInput) => void

function RecipientSelect({
  options,
  draft,
  setDraft,
  big = false,
}: {
  options: NotificationOptions
  draft: NotificationInput
  setDraft: Setter
  big?: boolean
}) {
  return (
    <select
      className={big ? 'form-control' : 'form-control input-sm'}
      name="user_type"
      value={draft.user_type}
      onChange={(e) => setDraft({ ...draft, user_type: Number(e.target.value) })}
    >
      {options.userTypes.map((type) => (
        <option key={type.user_type_id} value={type.user_type_id}>
          {type.display_name}
        </option>
      ))}
    </select>
  )
}

function LanguageSelect({
  options,
  draft,
  setDraft,
  big = false,
}: {
  options: NotificationOptions
  draft: NotificationInput
  setDraft: Setter
  big?: boolean
}) {
  const names: Record<string, string> = { RO: 'RO (romanian)', EN: 'EN (english)' }

  return (
    <select
      className={big ? 'form-control' : 'form-control input-sm'}
      name="language"
      value={draft.language}
      onChange={(e) => setDraft({ ...draft, language: e.target.value })}
    >
      {options.languages.map((lang) => (
        <option key={lang} value={lang}>
          {big ? (names[lang] ?? lang) : lang}
        </option>
      ))}
    </select>
  )
}

function TemplateSelect({
  options,
  draft,
  setDraft,
  big = false,
}: {
  options: NotificationOptions
  draft: NotificationInput
  setDraft: Setter
  big?: boolean
}) {
  return (
    <select
      className={big ? 'form-control' : 'form-control input-sm'}
      name="template"
      value={draft.template_id}
      onChange={(e) => setDraft({ ...draft, template_id: Number(e.target.value) })}
    >
      {options.templates.map((template) => (
        <option key={template.ID} value={template.ID}>
          {template.template_name}
        </option>
      ))}
    </select>
  )
}

/**
 * `from_name` / `from_email`.
 *
 * Panoul desfasurat le scria "Default" si "Assignee"; fereastra de adaugat,
 * "Default name" si "Assignee name". Aceleasi doua numere, doua scrieri -
 * pastrate amandoua.
 */
function FromSelect({
  field,
  draft,
  setDraft,
  big = false,
  names = false,
}: {
  field: 'from_name' | 'from_email'
  draft: NotificationInput
  setDraft: Setter
  big?: boolean
  names?: boolean
}) {
  const word = field === 'from_name' ? 'name' : 'email'

  return (
    <select
      className={big ? 'form-control' : 'form-control input-sm'}
      name={field}
      value={draft[field]}
      onChange={(e) => setDraft({ ...draft, [field]: Number(e.target.value) })}
    >
      <option value={1}>{names ? `Default ${word}` : 'Default'}</option>
      <option value={2}>{names ? `Assignee ${word}` : 'Assignee'}</option>
    </select>
  )
}

/**
 * Conditiile pe care le stie evenimentul.
 *
 * Numele vin din `it_easeedesk_event_conditions`. Ecranul vechi desena o casuta
 * numai pentru `ticket_status` si `escalation_type`; pentru orice alt nume
 * scria eticheta si nimic sub ea, si asa ramane - o conditie pe care ecranul
 * n-o stie desena trebuie sa se VADA ca exista, fiindca ea opreste instiintarea
 * (`Plan::conditionsPass` refuza un nume nestiut).
 */
function Conditions({
  options,
  draft,
  setDraft,
  big = false,
}: {
  options: NotificationOptions
  draft: NotificationInput
  setDraft: Setter
  big?: boolean
}) {
  const set = (name: string, value: string) =>
    setDraft({ ...draft, conditions: { ...(draft.conditions ?? {}), [name]: value } })

  return (
    <>
      {options.eventConditions.map((name) => (
        <div key={name} className={big ? 'form-group' : 'col-md-3'} style={big ? undefined : { marginBottom: 10 }}>
          <label className={big ? 'control-label' : undefined}>
            {(name.charAt(0).toUpperCase() + name.slice(1)).replace(/_/g, ' ')}:
          </label>
          {name === 'ticket_status' && (
            <select
              className={big ? 'form-control' : 'form-control input-sm'}
              name={`conditions[${name}]`}
              value={draft.conditions?.[name] ?? ANY_STATUS}
              onChange={(e) => set(name, e.target.value)}
            >
              <option value={ANY_STATUS}>All statuses</option>
              {options.statuses.map((status) => (
                <option key={status.ID} value={String(status.ID)}>
                  {status.status_name}
                </option>
              ))}
            </select>
          )}
          {name === 'escalation_type' && (
            <select
              className={big ? 'form-control' : 'form-control input-sm'}
              name={`conditions[${name}]`}
              value={draft.conditions?.[name] ?? ''}
              onChange={(e) => set(name, e.target.value)}
            >
              <option value="">Select</option>
              {ESCALATION_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          )}
        </div>
      ))}
    </>
  )
}

function CompaniesSelect({
  id,
  options,
  draft,
  setDraft,
}: {
  /* Doua formulare ale aceleiasi pagini poarta casuta asta - cel de creare si
     cel de schimbare - deci id-ul vine de la ele. Acelasi id in doua locuri ar
     lega eticheta de casuta gresita. */
  id: string
  options: NotificationOptions
  draft: NotificationInput
  setDraft: Setter
}) {
  return (
    <SelectPicker
      id={id}
      className="form-control input-sm"
      name="companies[]"
      title="All companies"
      multiple
      actionsBox
      size={15}
      selectedTextFormat="count > 3"
      options={options.companies.map((company) => ({ value: String(company.ID), label: company.name }))}
      value={(draft.companies ?? []).map(String)}
      onChange={(next) => setDraft({ ...draft, companies: next.map(Number) })}
    />
  )
}

/* ------------------------------------------------------------------ ajutor */

function fromRule(rule: NotificationRule, mobile: boolean): NotificationInput {
  return {
    user_type: rule.user_type,
    language: rule.language,
    template_id: rule.template_id,
    from_name: rule.from_name,
    from_email: rule.from_email,
    add_signature: rule.add_signature,
    conditions: { ...rule.conditions },
    companies: mobile ? [] : [...rule.related_companies],
  }
}

/**
 * Randul e salvat, dar Redis n-a primit setarile.
 *
 * Coada citeste hash-ul `notification_settings:<sp>`, nu tabela; cand scrierea
 * in el pica, setarea exista si totusi nu lucreaza. Omul trebuie sa afle
 * atunci, nu peste doua zile, cand suna clientul ca n-a primit emailul.
 */
function cacheWarning(result: NotificationSaved): string | null {
  return result.cached === false ? 'Saved, but the notification cache was not refreshed.' : null
}
